import { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  NoteAdd,
  TextFields,
  Timeline,
  CheckBoxOutlineBlank,
  RadioButtonUnchecked,
  ZoomIn,
  ZoomOut,
  FitScreen,
  Download,
  Upload,
  Share,
  CallMade,
  Image,
  InsertDriveFile,
  PhotoCamera,
} from '@mui/icons-material';
import { SvgIcon } from '@mui/material';

// Custom 4-sided diamond icon (rotated square shape)
const DiamondShapeIcon = () => (
  <SvgIcon>
    <path d="M12 2 L22 12 L12 22 L2 12 Z" fill="none" stroke="currentColor" strokeWidth="2" />
  </SvgIcon>
);

type ToolType = 'select' | 'postit' | 'text' | 'line' | 'box' | 'ellipse' | 'diamond' | 'connector';

type ExportFormat = 'tspace' | 'png' | 'svg';

type ToolbarProps = {
  activeTool: ToolType;
  onToolChange: (tool: ToolType) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onExport: (format: ExportFormat) => void;
  onImport: () => void;
  onShare: () => void;
  onImage: () => void;
  canEdit: boolean;
  isOwner: boolean;
};

export const Toolbar = ({
  activeTool,
  onToolChange,
  zoom,
  onZoomChange,
  onExport,
  onImport,
  onShare,
  onImage,
  canEdit,
  isOwner,
}: ToolbarProps) => {
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 16, md: 24 },
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        backgroundColor: 'white',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        p: 1,
        zIndex: 1000,
        flexWrap: 'wrap',
        justifyContent: 'center',
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      {canEdit && (
        <>
          <ToggleButtonGroup
            value={activeTool}
            exclusive
            onChange={(_, value) => value && onToolChange(value)}
            size="small"
          >
            <ToggleButton value="postit" aria-label="Add sticky note">
              <Tooltip title="Sticky Note">
                <NoteAdd />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="text" aria-label="Add text">
              <Tooltip title="Text">
                <TextFields />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="line" aria-label="Draw line">
              <Tooltip title="Line">
                <Timeline />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="box" aria-label="Draw box">
              <Tooltip title="Box">
                <CheckBoxOutlineBlank />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="ellipse" aria-label="Draw ellipse">
              <Tooltip title="Ellipse">
                <RadioButtonUnchecked />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="diamond" aria-label="Draw diamond">
              <Tooltip title="Diamond">
                <DiamondShapeIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="connector" aria-label="Draw connector">
              <Tooltip title="Connector">
                <CallMade />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Image Library">
            <IconButton onClick={onImage} size="small">
              <Image />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />
        </>
      )}

      {/* Zoom controls */}
      <Tooltip title="Zoom Out">
        <IconButton onClick={() => onZoomChange(Math.max(0.25, zoom - 0.25))} size="small">
          <ZoomOut />
        </IconButton>
      </Tooltip>
      <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center' }}>
        {Math.round(zoom * 100)}%
      </Typography>
      <Tooltip title="Zoom In">
        <IconButton onClick={() => onZoomChange(Math.min(2, zoom + 0.25))} size="small">
          <ZoomIn />
        </IconButton>
      </Tooltip>
      <Tooltip title="Fit to Screen">
        <IconButton onClick={() => onZoomChange(1)} size="small">
          <FitScreen />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      {/* Export/Import */}
      <Tooltip title="Export">
        <IconButton onClick={(e) => setExportAnchor(e.currentTarget)} size="small">
          <Download />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MenuItem onClick={() => { setExportAnchor(null); onExport('tspace'); }}>
          <ListItemIcon><InsertDriveFile fontSize="small" /></ListItemIcon>
          <ListItemText>ThinkSpace Format (.tspace)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setExportAnchor(null); onExport('png'); }}>
          <ListItemIcon><PhotoCamera fontSize="small" /></ListItemIcon>
          <ListItemText>PNG Image (.png)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setExportAnchor(null); onExport('svg'); }}>
          <ListItemIcon><Image fontSize="small" /></ListItemIcon>
          <ListItemText>SVG Vector (.svg)</ListItemText>
        </MenuItem>
      </Menu>
      {canEdit && (
        <Tooltip title="Import (.tspace)">
          <IconButton onClick={onImport} size="small">
            <Upload />
          </IconButton>
        </Tooltip>
      )}

      {/* Share */}
      {isOwner && (
        <Tooltip title="Share">
          <IconButton onClick={onShare} size="small" color="primary">
            <Share />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
